// Task 188 — shared table between the EmployeeDetailPage "Needs Your
// Review" top-N summary and the EmployeeReviewPage full-list page.
//
// Task 192 (D3 + D4 + D6) — adds the bulk-classify + per-row classify
// flows + per-row screenshot expansion. The component is shared, so
// both surfaces (the top-N summary and the full-list page) inherit the
// new behaviour automatically. Selection state and expansion state are
// independent — checking a row does not toggle expansion, and vice
// versa.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ReviewItem } from '@/data/employeeDetail';
import {
  ClassifyGroupsModal,
  type ClassifyGroup,
  type ClassifyGroupsModalMode,
} from '@/components/composites/ClassifyGroupsModal';
import { ScreenshotImage } from '@/components/composites/ScreenshotImage';
import {
  ScreenshotLightbox,
  type ScreenshotLightboxEntry,
} from '@/components/composites/ScreenshotLightbox';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import { formatDateTimeInTz } from '@/lib/dates';
import styles from './UnmatchedGroupsTable.module.css';

/**
 * Two render modes:
 *   - `top-n` — "Showing top 4 unmatched groups by time" with a trailing
 *     "see all N" link when `totalGroups > items.length`. When totals
 *     match, the link is suppressed and only the prefix renders.
 *   - `all`  — "Showing all N unmatched groups" with no trailing link.
 *
 * Per-employee scoping: the table only renders for one employee at a
 * time; the `employeeId` prop is required so D5's modal and D6's
 * expansion hit the right URL.
 */
type UnmatchedGroupsTableProps = {
  items: readonly ReviewItem[];
  employeeId: string;
} & (
  | { mode: 'top-n'; totalGroups: number; seeAllHref: string }
  | { mode: 'all'; totalGroups: number }
);

interface InternalGroupResponse {
  events: Array<{
    id: string;
    occurredAt: string;
    screenshotId: string | null;
    insightfulScreenshotId: string | null;
  }>;
  total: number;
}

// (app, title) pair as the row key — same pair used as `tr key=` today,
// hoisted into a helper so selection-state and expansion-state both key
// off it consistently.
function rowKey(item: { app: string; title: string }): string {
  return `${item.app} ${item.title}`;
}

export function UnmatchedGroupsTable(props: UnmatchedGroupsTableProps) {
  const { items, employeeId } = props;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [modalState, setModalState] = useState<{
    mode: ClassifyGroupsModalMode;
    groups: readonly ClassifyGroup[];
  } | null>(null);

  const allKeys = useMemo(() => items.map((i) => rowKey(i)), [items]);

  const totalSelectable = items.length;
  const selectedCount = Array.from(selected).filter((k) => allKeys.includes(k)).length;
  const headerState: 'none' | 'partial' | 'all' =
    selectedCount === 0 ? 'none' : selectedCount === totalSelectable ? 'all' : 'partial';

  const toggleRow = (key: string, value: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (value) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const handleHeaderToggle = () => {
    if (headerState === 'none') {
      setSelected(new Set(allKeys));
    } else {
      // partial OR all → clear
      setSelected(new Set());
    }
  };

  const toggleExpansion = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const openBulkModal = () => {
    const groups = items
      .filter((i) => selected.has(rowKey(i)))
      .map((i) => ({ app: i.app, title: i.title }));
    if (groups.length === 0) return;
    setModalState({ mode: 'bulk', groups });
  };

  const openSingleModal = (item: { app: string; title: string }) => {
    setModalState({ mode: 'single', groups: [{ app: item.app, title: item.title }] });
  };

  const handleModalClose = () => {
    setModalState(null);
  };

  // On successful classify: clear selection + collapse any expanded rows
  // that just got classified. The query invalidation inside the modal
  // handles refetching the table so the rows fall out.
  const handleModalSuccess = () => {
    if (modalState) {
      const classifiedKeys = new Set(modalState.groups.map((g) => rowKey(g)));
      setSelected((prev) => {
        const next = new Set(prev);
        for (const k of classifiedKeys) next.delete(k);
        return next;
      });
      setExpanded((prev) => {
        const next = new Set(prev);
        for (const k of classifiedKeys) next.delete(k);
        return next;
      });
    }
  };

  return (
    <>
      <div className={styles.tableWrap}>
        <div className={styles.reviewTableNote}>
          {props.mode === 'top-n' ? (
            <>
              Showing top {items.length} unmatched groups by time
              {props.totalGroups > items.length && (
                <>
                  {' '}
                  &mdash;{' '}
                  <Link to={props.seeAllHref} className={styles.linkArrow}>
                    see all {props.totalGroups}
                  </Link>
                </>
              )}
            </>
          ) : (
            <>Showing all {props.totalGroups} unmatched groups</>
          )}
        </div>

        {selectedCount > 0 && (
          <div
            className={styles.bulkActionBar}
            role="region"
            aria-label="Bulk classify"
            data-testid="bulk-action-bar"
          >
            <button type="button" className={styles.btnBulkClassify} onClick={openBulkModal}>
              Classify selected ({selectedCount})
            </button>
            <button
              type="button"
              className={styles.btnClearSelection}
              onClick={() => setSelected(new Set())}
            >
              Clear
            </button>
          </div>
        )}

        <table className={styles.table}>
          <thead>
            <tr>
              <th aria-label="Expand row" className={styles.colChevron} />
              <th className={styles.colCheckbox}>
                <HeaderCheckbox state={headerState} onToggle={handleHeaderToggle} />
              </th>
              <th>Application &amp; Window Title</th>
              <th className={styles.alignRight}>Sessions</th>
              <th className={styles.alignRight}>Total Time</th>
              <th>Date Range</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const key = rowKey(item);
              const isSelected = selected.has(key);
              const isExpanded = expanded.has(key);
              return (
                <RowGroup
                  key={key}
                  item={item}
                  rowKeyValue={key}
                  isSelected={isSelected}
                  isExpanded={isExpanded}
                  onToggleSelect={(v) => toggleRow(key, v)}
                  onToggleExpand={() => toggleExpansion(key)}
                  onClassifyClick={() => openSingleModal(item)}
                  employeeId={employeeId}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      {modalState && (
        <ClassifyGroupsModal
          open
          mode={modalState.mode}
          groups={modalState.groups}
          employeeId={employeeId}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </>
  );
}

// ── Internal row component ──────────────────────────────────────────────
//
// Hoisted so the per-row `useQuery` for the screenshot expansion only
// fires when the row is actually expanded (`enabled: isExpanded`).
// React Query keys on (orgId, employeeId, app, title, dateRange) so
// independently expanded rows have independent cache entries.

interface RowGroupProps {
  item: ReviewItem;
  rowKeyValue: string;
  isSelected: boolean;
  isExpanded: boolean;
  onToggleSelect: (v: boolean) => void;
  onToggleExpand: () => void;
  onClassifyClick: () => void;
  employeeId: string;
}

function RowGroup({
  item,
  rowKeyValue,
  isSelected,
  isExpanded,
  onToggleSelect,
  onToggleExpand,
  onClassifyClick,
  employeeId,
}: RowGroupProps) {
  const { mockupMode, currentOrg, dateRange } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;
  // The drill-down endpoint doesn't return the org timezone (the
  // /events/internal-group payload is event-list, not aggregate). Default
  // to UTC for the lightbox's date-stamp render — when the operator
  // actually needs precise local time, the dashboard surfaces that on
  // the app-title-log page where tz is wired through. The use case here
  // is "see what the screenshot looked like at roughly this time," not
  // legal-grade timestamps.
  const tz = 'UTC';

  // Lightbox state — local to the row so opening the lightbox in one
  // expanded row does not race against another row's state.
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const expansionQuery = useQuery({
    queryKey: ['employeeInternalGroup', orgId, employeeId, item.app, item.title, dateRange],
    queryFn: async (): Promise<InternalGroupResponse> => {
      const token = await getToken();
      const qs = new URLSearchParams();
      qs.set('app', item.app);
      qs.set('title', item.title);
      if (dateRange.start) qs.set('startDate', dateRange.start);
      if (dateRange.end) qs.set('endDate', dateRange.end);
      return apiFetch<InternalGroupResponse>(
        `/api/v1/orgs/${orgId}/employees/${employeeId}/events/internal-group?${qs.toString()}`,
        token,
      );
    },
    enabled: !mockupMode && isExpanded && !!orgId && !!employeeId,
  });

  const expansionEvents = expansionQuery.data?.events ?? [];
  const screenshotEvents = expansionEvents.filter((e) => e.screenshotId !== null);
  const total = expansionQuery.data?.total ?? 0;

  // Lightbox entries derived from the screenshot-bearing events. The
  // index passed to the lightbox refers to this filtered array — clicks
  // pass the index of the clicked thumbnail.
  const lightboxEntries: ScreenshotLightboxEntry[] = screenshotEvents.map((e) => ({
    screenshotId: e.screenshotId as string,
    occurredAt: e.occurredAt,
    employeeName: null,
    altText: `${item.app}${item.title ? ' — ' + item.title : ''}`,
  }));

  return (
    <>
      <tr key={rowKeyValue} data-testid={`unmatched-row-${item.app}-${item.title}`}>
        <td className={styles.colChevron}>
          <button
            type="button"
            className={styles.btnChevron}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse screenshots' : 'Expand screenshots'}
            onClick={onToggleExpand}
          >
            <span aria-hidden="true">{isExpanded ? '▾' : '▸'}</span>
          </button>
        </td>
        <td className={styles.colCheckbox}>
          {/* aria-label-only checkbox: the row text already conveys what
              is being selected; a visible label would duplicate it and
              also break tests that getByText('Microsoft Word'). */}
          <input
            type="checkbox"
            className={styles.rowCheckbox}
            checked={isSelected}
            onChange={(e) => onToggleSelect(e.target.checked)}
            aria-label={`Select ${item.app}${item.title ? ' — ' + item.title : ''}`}
          />
        </td>
        <td>
          <div className={styles.reviewAppName}>
            {item.app} &mdash; {item.title}
          </div>
          <div className={styles.reviewAppSub}>
            {item.screenshots} screenshots &middot; last seen {item.lastSeen}
          </div>
        </td>
        <td className={`${styles.mono} ${styles.alignRight}`}>{item.sessions}</td>
        <td className={`${styles.mono} ${styles.alignRight}`}>{item.time}</td>
        <td className={styles.reviewDate}>{item.dateRange}</td>
        <td>
          <button type="button" className={styles.btnAssign} onClick={onClassifyClick}>
            Classify
          </button>
        </td>
      </tr>
      {isExpanded && (
        <tr className={styles.expansionRow} data-testid={`expansion-${item.app}-${item.title}`}>
          <td colSpan={7} className={styles.expansionCell}>
            {mockupMode ? (
              <p className={styles.muted}>Screenshot expansion is live-mode only.</p>
            ) : expansionQuery.isLoading ? (
              <div
                className={styles.screenshotGrid}
                data-testid="expansion-skeleton"
                aria-busy="true"
              >
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className={styles.screenshotSkeleton} />
                ))}
              </div>
            ) : screenshotEvents.length === 0 ? (
              <p className={styles.muted}>No screenshots for this group.</p>
            ) : (
              <>
                <div className={styles.screenshotGrid}>
                  {screenshotEvents.map((e, idx) => (
                    <div key={e.id} className={styles.screenshotTile}>
                      <ScreenshotImage
                        screenshotId={e.screenshotId as string}
                        width={160}
                        height={90}
                        alt={`${item.app} — ${formatDateTimeInTz(e.occurredAt, tz)}`}
                        onClick={() => setLightboxIndex(idx)}
                        clickAriaLabel={`Open screenshot from ${formatDateTimeInTz(e.occurredAt, tz)} in full size`}
                      />
                    </div>
                  ))}
                </div>
                {total > screenshotEvents.length && (
                  <p className={styles.muted}>
                    Showing first {screenshotEvents.length} of {total}
                  </p>
                )}
              </>
            )}
          </td>
        </tr>
      )}
      {lightboxIndex !== null && (
        <ScreenshotLightbox
          open
          entries={lightboxEntries}
          index={lightboxIndex}
          tz={tz}
          onClose={() => setLightboxIndex(null)}
          onPrev={() =>
            setLightboxIndex((idx) =>
              idx === null ? null : (idx - 1 + lightboxEntries.length) % lightboxEntries.length,
            )
          }
          onNext={() =>
            setLightboxIndex((idx) => (idx === null ? null : (idx + 1) % lightboxEntries.length))
          }
        />
      )}
    </>
  );
}

// ── Tri-state header checkbox ───────────────────────────────────────────
//
// Standard table semantics: unchecked → check all; partial → clear all;
// all → clear all. The native input's `indeterminate` property is the
// only way to render the dash (CSS-only doesn't reach assistive tech),
// so we set it imperatively after mount.

interface HeaderCheckboxProps {
  state: 'none' | 'partial' | 'all';
  onToggle: () => void;
}

function HeaderCheckbox({ state, onToggle }: HeaderCheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = state === 'partial';
    }
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className={styles.rowCheckbox}
      checked={state === 'all'}
      onChange={onToggle}
      aria-label={state === 'none' ? 'Select all rows' : 'Clear row selection'}
    />
  );
}
